if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this._variants = null;
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      get variants() {
        if (this._variants) return this._variants;
        const variantData = this.querySelector('script[data-product-variants]');
        if (!variantData) return (this._variants = []);
        try {
          this._variants = JSON.parse(variantData.textContent || '[]');
        } catch (error) {
          console.error('Unable to parse product variant availability data.', error);
          this._variants = [];
        }
        return this._variants;
      }

      get optionGroups() {
        return Array.from(this.querySelectorAll('.product-form__input[data-option-position]'))
          .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
      }

      get styleGroup() {
        return this.optionGroups.find((group) => (group.dataset.optionName || '').trim().toLowerCase() === 'style') || null;
      }

      get colorGroup() {
        return this.optionGroups.find((group) => {
          const name = (group.dataset.optionName || '').trim().toLowerCase();
          return name === 'color' || name === 'colour';
        }) || null;
      }

      get sizeGroup() {
        return this.optionGroups.find((group) => (group.dataset.optionName || '').trim().toLowerCase() === 'size') || null;
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        if (this._hasChangeListener) return;
        this._hasChangeListener = true;

        this.updateOptionAvailability(true);

        this.addEventListener('change', (event) => {
          const targetInThisElement = this.contains(event.target);
          let hasNestedVariantSelects = false;
          let currentElement = event.target;

          while (currentElement && currentElement !== document.body) {
            if (currentElement.tagName === 'VARIANT-SELECTS') {
              if (currentElement !== this) hasNestedVariantSelects = true;
              break;
            }
            currentElement = currentElement.parentElement;
          }

          if (!targetInThisElement || hasNestedVariantSelects) return;

          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);

          // Style is the master selector. It is never disabled by Color or Size.
          // If a Style change makes the old Color or Size invalid, move those
          // subordinate selectors to the first real available combination.
          this.updateOptionAvailability(true);

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        }, true);
      }

      getGroupValue(group) {
        if (!group) return null;
        const select = group.querySelector('select');
        if (select) return select.value || null;
        return group.querySelector('input[type="radio"]:checked')?.value || null;
      }

      setGroupValue(group, value) {
        if (!group || value == null) return false;
        const select = group.querySelector('select');

        if (select) {
          const option = Array.from(select.options).find((item) => item.value === value && !item.disabled);
          if (!option) return false;
          Array.from(select.options).forEach((item) => item.removeAttribute('selected'));
          select.value = value;
          option.setAttribute('selected', 'selected');
          this.refreshGroupSelectedLabel(group, value);
          return true;
        }

        const input = Array.from(group.querySelectorAll('input[type="radio"]'))
          .find((item) => item.value === value && !item.disabled);
        if (!input) return false;
        input.checked = true;
        this.refreshGroupSelectedLabel(group, value);
        return true;
      }

      refreshGroupSelectedLabel(group, value) {
        const selectedLabel = group?.querySelector('[data-selected-value], [data-selected-swatch-value]');
        if (selectedLabel && !selectedLabel.querySelector('.swatch')) selectedLabel.textContent = value;
      }

      variantHas(variant, position, value) {
        return value == null || variant.options[position - 1] === value;
      }

      availableVariantsMatching(requirements = {}) {
        return this.variants.filter((variant) => {
          if (!variant.available) return false;
          return Object.entries(requirements).every(([position, value]) =>
            this.variantHas(variant, Number(position), value)
          );
        });
      }

      setGroupAvailability(group, availableValues, forceEnabled = false) {
        if (!group) return;
        const select = group.querySelector('select');

        if (select) {
          Array.from(select.options).forEach((option) => {
            const available = forceEnabled || availableValues.has(option.value);
            option.disabled = !available;
            option.classList.toggle('disabled', !available);
            option.setAttribute('aria-disabled', String(!available));
          });
          return;
        }

        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const available = forceEnabled || availableValues.has(input.value);
          input.disabled = !available;
          input.classList.toggle('disabled', !available);
          input.setAttribute('aria-disabled', String(!available));

          const label = group.querySelector(`label[for="${CSS.escape(input.id)}"]`);
          if (label) {
            label.classList.toggle('disabled', !available);
            label.setAttribute('aria-disabled', String(!available));
          }
        });
      }

      firstAvailableValue(group, variants) {
        if (!group) return null;
        const position = Number(group.dataset.optionPosition);
        const values = new Set(variants.map((variant) => variant.options[position - 1]));
        const select = group.querySelector('select');
        if (select) return Array.from(select.options).find((option) => values.has(option.value))?.value || null;
        return Array.from(group.querySelectorAll('input[type="radio"]')).find((input) => values.has(input.value))?.value || null;
      }

      updateOptionAvailability(repairInvalidSelections = false) {
        if (!this.variants.length) return;

        const styleGroup = this.styleGroup;
        const colorGroup = this.colorGroup;
        const sizeGroup = this.sizeGroup;

        // Products without a Style option keep the older all-options behavior.
        if (!styleGroup) {
          this.updateGenericAvailability();
          return;
        }

        const stylePosition = Number(styleGroup.dataset.optionPosition);
        const colorPosition = colorGroup ? Number(colorGroup.dataset.optionPosition) : null;
        const sizePosition = sizeGroup ? Number(sizeGroup.dataset.optionPosition) : null;
        const selectedStyle = this.getGroupValue(styleGroup);

        // STYLE IS MASTER: every Style value that has at least one available variant
        // remains clickable regardless of the currently selected Color or Size.
        const globallyAvailableStyles = new Set(
          this.availableVariantsMatching().map((variant) => variant.options[stylePosition - 1])
        );
        this.setGroupAvailability(styleGroup, globallyAvailableStyles, false);

        const styleVariants = this.availableVariantsMatching({ [stylePosition]: selectedStyle });

        // COLOR depends only on Style, never on the old Size selection.
        if (colorGroup) {
          const colorsForStyle = new Set(styleVariants.map((variant) => variant.options[colorPosition - 1]));
          this.setGroupAvailability(colorGroup, colorsForStyle);

          const currentColor = this.getGroupValue(colorGroup);
          if (repairInvalidSelections && !colorsForStyle.has(currentColor)) {
            const replacementColor = this.firstAvailableValue(colorGroup, styleVariants);
            if (replacementColor) this.setGroupValue(colorGroup, replacementColor);
          }
        }

        const selectedColor = this.getGroupValue(colorGroup);

        // SIZE depends on Style + Color. This prevents a size that exists for the
        // style but not for the currently selected color from being purchasable.
        if (sizeGroup) {
          const requirements = { [stylePosition]: selectedStyle };
          if (colorGroup && selectedColor) requirements[colorPosition] = selectedColor;
          const colorStyleVariants = this.availableVariantsMatching(requirements);
          const sizesForSelection = new Set(colorStyleVariants.map((variant) => variant.options[sizePosition - 1]));
          this.setGroupAvailability(sizeGroup, sizesForSelection);

          const currentSize = this.getGroupValue(sizeGroup);
          if (repairInvalidSelections && !sizesForSelection.has(currentSize)) {
            const replacementSize = this.firstAvailableValue(sizeGroup, colorStyleVariants);
            if (replacementSize) this.setGroupValue(sizeGroup, replacementSize);
          }
        }

        // Any extra option groups are constrained by Style plus the currently
        // selected Color and Size, but they can never disable Style itself.
        const knownGroups = new Set([styleGroup, colorGroup, sizeGroup].filter(Boolean));
        this.optionGroups.filter((group) => !knownGroups.has(group)).forEach((group) => {
          const position = Number(group.dataset.optionPosition);
          const requirements = { [stylePosition]: selectedStyle };
          if (colorGroup && this.getGroupValue(colorGroup)) requirements[colorPosition] = this.getGroupValue(colorGroup);
          if (sizeGroup && this.getGroupValue(sizeGroup)) requirements[sizePosition] = this.getGroupValue(sizeGroup);
          const matching = this.availableVariantsMatching(requirements);
          const values = new Set(matching.map((variant) => variant.options[position - 1]));
          this.setGroupAvailability(group, values);
        });
      }

      updateGenericAvailability() {
        const selections = {};
        this.optionGroups.forEach((group) => {
          selections[Number(group.dataset.optionPosition)] = this.getGroupValue(group);
        });

        this.optionGroups.forEach((group) => {
          const position = Number(group.dataset.optionPosition);
          const values = new Set();
          this.variants.forEach((variant) => {
            if (!variant.available) return;
            const matchesOtherSelections = this.optionGroups.every((otherGroup) => {
              const otherPosition = Number(otherGroup.dataset.optionPosition);
              if (otherPosition === position) return true;
              const selected = selections[otherPosition];
              return !selected || variant.options[otherPosition - 1] === selected;
            });
            if (matchesOtherSelections) values.add(variant.options[position - 1]);
          });
          this.setGroupAvailability(group, values);
        });
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          const previouslySelected = Array.from(target.options).find((option) => option.getAttribute('selected'));
          if (previouslySelected) previouslySelected.removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;

          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest('.product-form__input').querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
